import { PropsWithChildren, useEffect } from 'react';
import queryString from 'query-string';
import { get } from 'lodash';
import { CountryCode, ConsumerOnboarding } from '@dagensmat/core';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Typography } from '@dagensmat/carrot/Components';
import { postConsumerOnboardingUpdate } from 'api/consumer';
import { consumerOnboardingUpdated } from '_common/reducers/auth';
import useHideHeader from '_common/hooks/useHideHeader';
import { hasConsumerOnboarded } from 'utils/role';
import PageOne from './PageOne';
import PageTwo from './PageTwo';
import { Page } from '@components/page';
import { useMediaQuery } from '../../../carrot/utils/media-query';
import { Container } from '@dagensmat/carrot/Layout';
import {
  useAppDispatch,
  useAppSelector
} from '../../../_common/hooks/reduxHooks';

const ContentWrapper = ({ children }: PropsWithChildren) => {
  const { tabletUp } = useMediaQuery();
  return <Container my={tabletUp ? 'xl' : 's'}>{children}</Container>;
};

const OnboardingPage = () => {
  const { contactPerson, country, hasOnboarded, name, userId } = useAppSelector(
    ({
      auth: { _id: userId, contactPerson, name, onboarding = {}, organization }
    }) => {
      return {
        userId,
        contactPerson,
        name,
        hasOnboarded: hasConsumerOnboarded(onboarding),
        country: get(organization, 'address.country', CountryCode.NO)
      };
    }
  );
  const dispatch = useAppDispatch();
  const { step } = useParams();
  const { set: setHideHeader, unset: unsetHideHeader } = useHideHeader();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { returnTo = '' } = queryString.parse(search);

  useEffect(() => {
    setHideHeader();
    return () => {
      unsetHideHeader();
    };
  }, []);

  let currentStep = step !== undefined ? parseInt(step, 10) || 1 : 1;

  const steps = [PageOne, PageTwo];

  if (currentStep > steps.length || currentStep < 0) {
    navigate(`/${returnTo}`);
    return null;
  }

  const PageToShow = steps[currentStep - 1];

  const finishConsumerOnboarding = () => {
    if (!hasOnboarded) {
      postConsumerOnboardingUpdate({
        userId,
        onboardingStep: ConsumerOnboarding.HAS_SEEN_INTRO_SCREEN
      });
      dispatch(
        consumerOnboardingUpdated(ConsumerOnboarding.HAS_SEEN_INTRO_SCREEN)
      );
    }
    navigate(`/${returnTo}`);
  };

  return (
    <Page header={null} bottom={null}>
      <ContentWrapper>
        <Typography variant="allCapsTitle">
          {currentStep}/{steps.length}
        </Typography>
        <PageToShow
          contactPerson={contactPerson}
          name={name}
          next={() => {
            currentStep += 1;

            if (currentStep > steps.length) {
              finishConsumerOnboarding();
            } else {
              navigate(`/onboarding/${currentStep}${search}`);
            }
          }}
          skip={() => {
            finishConsumerOnboarding();
          }}
          country={country}
        />
      </ContentWrapper>
    </Page>
  );
};

export default OnboardingPage;
